import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { HelpCircle, CheckCircle, XCircle } from 'lucide-react';
import { XpFlashToast, addSessionXp } from './XpFlashToast';
import { detectContentCategory } from './ContinueLearningWidget';

interface QuizQuestion {
  question: string;
  options: string[];
  correct: number; // 0-based index
}

const QUIZ_BANK: Record<string, QuizQuestion[]> = {
  bikes: [
    { question: 'What does ABS stand for?', options: ['Anti-lock Braking System', 'Auto Brake Sensor', 'Adaptive Braking Support', 'All-wheel Brake System'], correct: 0 },
    { question: 'Which metric measures engine twist force?', options: ['BHP', 'RPM', 'Torque (Nm)', 'CC'], correct: 2 },
    { question: 'What does a slipper clutch help prevent?', options: ['Engine overheat', 'Rear-wheel hop during downshifts', 'Chain wear', 'Low fuel consumption'], correct: 1 },
    { question: 'What is kerb weight?', options: ['Weight without fuel', 'Weight with full tank + fluids', 'Dry engine weight', 'Payload capacity'], correct: 1 },
    { question: 'BS7 emission norms replace which standard in India?', options: ['BS4', 'BS5', 'BS6', 'Euro 5'], correct: 2 },
  ],
  cars: [
    { question: 'What does ADAS stand for?', options: ['Automatic Drive Assist System', 'Advanced Driver Assistance Systems', 'Adaptive Drive Auto Steer', 'Auto Detection Alert System'], correct: 1 },
    { question: 'NCAP safety ratings go up to how many stars?', options: ['3', '4', '5', '6'], correct: 2 },
    { question: 'What does CVT stand for in transmissions?', options: ['Constant Velocity Torque', 'Continuously Variable Transmission', 'Central Variable Throttle', 'Computer Verified Traction'], correct: 1 },
    { question: 'Which fuel type produces zero tailpipe emissions?', options: ['CNG', 'Diesel', 'Electric', 'Hybrid'], correct: 2 },
    { question: 'Ex-showroom price excludes which cost?', options: ['GST', 'Registration & insurance', 'Manufacturing', 'Dealer margin'], correct: 1 },
  ],
  ev: [
    { question: 'What unit measures EV battery capacity?', options: ['CC', 'kWh', 'BHP', 'Nm'], correct: 1 },
    { question: 'Regenerative braking converts kinetic energy to what?', options: ['Heat', 'Electrical energy', 'Sound', 'Fuel'], correct: 1 },
    { question: 'FAME II subsidy in India applies to which vehicles?', options: ['All vehicles', 'Only cars', 'EVs meeting criteria', 'Only buses'], correct: 2 },
    { question: 'What does DC fast charging provide vs AC?', options: ['Slower charge', 'Same speed', 'Faster charge', 'Wireless charge'], correct: 2 },
  ],
  guides: [
    { question: 'What is the typical EV battery warranty in India?', options: ['3 years', '5 years', '8 years', '10 years'], correct: 2 },
    { question: 'Which body type has the lowest drag coefficient?', options: ['SUV', 'Sedan', 'Hatchback', 'Coupe'], correct: 3 },
  ],
  data: [
    { question: 'FY2026 in India runs from which months?', options: ['Jan–Dec 2026', 'Apr 2025–Mar 2026', 'Jul 2025–Jun 2026', 'Apr 2026–Mar 2027'], correct: 1 },
    { question: 'Which segment leads India 2W sales volume?', options: ['Premium (>250cc)', 'Scooters', 'Entry commuters (100-110cc)', 'Adventure'], correct: 2 },
  ],
  scooters: [
    { question: 'What is the most common scooter engine size in India?', options: ['50cc', '110cc', '125cc', '150cc'], correct: 2 },
    { question: 'Which braking system is mandatory on 125cc+ scooters?', options: ['Drum only', 'CBS or ABS', 'Disc only', 'Regenerative'], correct: 1 },
  ],
};

const ANSWERED_KEY = 'mw-quiz-answered';
const XP_PER_CORRECT = 10;

function getAnsweredIds(): string[] {
  try {
    const raw = localStorage.getItem(ANSWERED_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function markAnswered(qId: string): void {
  const ids = getAnsweredIds();
  if (!ids.includes(qId)) {
    ids.push(qId);
    localStorage.setItem(ANSWERED_KEY, JSON.stringify(ids));
  }
}

function pickQuestion(category: string): { question: QuizQuestion; id: string } | null {
  const bank = QUIZ_BANK[category];
  if (!bank || bank.length === 0) return null;

  const answered = new Set(getAnsweredIds());
  const available = bank
    .map((q, i) => ({ question: q, id: `${category}-${i}` }))
    .filter((entry) => !answered.has(entry.id));

  if (available.length === 0) return null;
  return available[Math.floor(Math.random() * available.length)];
}

export function MicroQuiz() {
  const location = useLocation();
  const [entry, setEntry] = useState<{ question: QuizQuestion; id: string } | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [xpTrigger, setXpTrigger] = useState(0);

  // Pick a new question when page changes
  useEffect(() => {
    const cat = detectContentCategory(location.pathname);
    if (!cat) {
      setEntry(null);
      return;
    }
    setEntry(pickQuestion(cat));
    setSelected(null);
  }, [location.pathname]);

  const handleAnswer = useCallback((index: number) => {
    if (selected !== null || !entry) return;
    setSelected(index);
    markAnswered(entry.id);
    if (index === entry.question.correct) {
      addSessionXp(XP_PER_CORRECT);
      setXpTrigger((t) => t + 1);
    }
  }, [selected, entry]);

  if (!entry) return null;

  const { question } = entry;
  const isCorrect = selected === question.correct;
  const isAnswered = selected !== null;

  return (
    <>
      <div className="rounded-2xl border border-border-primary bg-bg-secondary overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border-primary bg-gradient-to-r from-indigo-500/5 to-transparent">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-500/10">
            <HelpCircle className="h-4 w-4 text-indigo-500" />
          </div>
          <h3 className="font-semibold text-text-primary text-sm">Quick Quiz</h3>
          {isAnswered && (
            <span className={`ml-auto text-xs font-semibold ${isCorrect ? 'text-green-500' : 'text-red-400'}`}>
              {isCorrect ? '+10 XP' : 'Try next time!'}
            </span>
          )}
        </div>

        <div className="px-4 py-3">
          <p className="text-sm font-medium text-text-primary mb-3">{question.question}</p>
          <div className="space-y-2">
            {question.options.map((opt, i) => {
              let classes = 'w-full text-left rounded-lg border px-3 py-2 text-sm transition-colors ';
              if (!isAnswered) {
                classes += 'border-border-primary bg-bg-primary hover:bg-bg-tertiary text-text-secondary cursor-pointer';
              } else if (i === question.correct) {
                classes += 'border-green-500/30 bg-green-500/10 text-green-400';
              } else if (i === selected) {
                classes += 'border-red-500/30 bg-red-500/10 text-red-400';
              } else {
                classes += 'border-border-primary bg-bg-primary text-text-tertiary opacity-50';
              }

              return (
                <button
                  key={i}
                  className={classes}
                  onClick={() => handleAnswer(i)}
                  disabled={isAnswered}
                >
                  <span className="flex items-center gap-2">
                    {isAnswered && i === question.correct && <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />}
                    {isAnswered && i === selected && i !== question.correct && <XCircle className="h-4 w-4 text-red-400 flex-shrink-0" />}
                    {opt}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <XpFlashToast amount={XP_PER_CORRECT} trigger={xpTrigger} />
    </>
  );
}
